import { ReactNode } from 'react'

import { SxProps, Theme } from '@mui/material'

import { OpenPositions } from '../../pages/Dashboard'
import { ImportHistory, LogHistory } from '../../pages/Settings/types'
import { Trade, TradeStatus, TradeAiAnalysis } from '../../pages/TradingLog/types'
import { SortDirection, SortDirectionCapital } from '../../types'

export interface TradeStatusCellProps {
  status: TradeStatus | boolean
}

export interface TableBodyRowProps<T> {
  logEntry: T
  columnOrder: (keyof T)[]
  onRowClick: () => void
  selectedRow: string | null
  displaySelectTradingLogRow?: boolean
  onAiAnalysis?: () => void
  showTradeInfoColumn?: boolean
  showAiAnalysisColumn?: boolean
  analysisLoading?: boolean
}

export interface TableBodyCellProps<T> {
  type: string
  value: T[keyof T]
  selectedRow: string | null
  currency: string
  error?: string | null
  onClick?: () => void
  id?: string
}

export interface ProfitCellProps {
  value: string
  currency: string
}

export interface JournalingCellProps {
  value: string
  selectedRow: string | null
  id?: string
}

export interface ImportStatusCellProps {
  value: boolean
  errorDescription: string | null | undefined
}

export interface TablePaginationProps {
  totalPages: number
  currentPage: number
  pageSize: number
  totalItems: number
  hasPreviousPage: boolean
  hasNextPage: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  itemsName?: string
}

export interface TableProps<T> {
  columnOrder: string[]
  sortingColumn: string | null
  sortingDirection: SortDirection | SortDirectionCapital
  onColumnReorder: (startIndex: number, endIndex: number) => void
  onSort: (column: keyof T) => void
  displayedTrades: T[]
  keyForRefresh?: string
  tableStyles?: SxProps<Theme>
  showTradeInfoColumn?: boolean
  showAiAnalysisColumn?: boolean
  onAnalysisSaved?: (tradeId: number, analysis: TradeAiAnalysis | null) => void
}

export type AvailableTableTypes = OpenPositions | Trade | LogHistory | ImportHistory

export enum CommonTableColumnsEnum {
  id = 'id',
  createdAt = 'createdAt',
  ticket = 'ticket',
  openTime = 'openTime',
  closeTime = 'closeTime',
  symbol = 'symbol',
  status = 'status',
  type = 'type',
  volume = 'volume',
  lots = 'lots',
  openPrice = 'openPrice',
  closePrice = 'closePrice',
  grossProfit = 'grossProfit',
  netProfit = 'netProfit',
  roi = 'roi',
  transactionStatus = 'transactionStatus',
  journalingCompletion = 'journalingCompletion',
  riskLevel = 'riskLevel',
  pips = 'pips',
  commissionsTotal = 'commissionsTotal',
  columns = 'columns',
  commissions = 'commissions',
  fee = 'fee',
  swap = 'swap',
  posValue = 'posValue',
  cashFlowType = 'cashFlowType',
  amount = 'amount',
  comment = 'comment',
  date = 'date',
  action = 'action',
  method = 'method',
  broker = 'broker',
  account = 'account',
  importStatus = 'importStatus',
  platform = 'platform',
}

export interface EmptyTableProps {
  title?: string | null
  message?: string | null
  children?: ReactNode
  sx?: SxProps
}

export interface RadioButtonProps {
  label: string
  checked: boolean
  width?: number
}

export type RadioButtonColors = {
  green?: string
  red?: string
  grey?: string
}

export interface CheckboxButtonProps {
  name: string
  label?: string | null
  defaultValue: boolean
  customValue?: boolean
  showDeleteButton?: boolean
  deleteCustomValue?: () => void
}

export interface JournalingModalProps {
  isOpen: boolean
  onClose: () => void
  tradingLogId: string | null
}

export enum CustomValueModalTypeEnum {
  TRADING_STRATEGY = 'Trading Strategy',
  TRADING_CONFLUENCES = 'Trading Confluences',
}

export interface NestedFields {
  [key: string]: string | boolean | null
}

export interface JournalingForm {
  isPositive: boolean | null
  emotionTags: {
    [key: string]: string | boolean | null
  }
  followedPlan: boolean | null
  brokenRules: {
    [key: string]: string | boolean | null
  }
  journalNote: string
  positiveComments: {
    [key: string]: string | boolean | null
  }
  negativeComments: {
    [key: string]: string | boolean | null
  }
  tradingStrategy: {
    [key: string]: string | boolean | null
  }
  customTradingStrategy: {
    [key: string]: string | boolean | null
  }
  tradingConfluence: {
    [key: string]: string | boolean | null
  }
  customTradingConfluence: {
    [key: string]: string | boolean | null
  }
  riskManagement: {
    [key: string]: string | boolean | null
  }
}

export interface JournalingRequestData {
  tradingLogId: string
  psychology: {
    isPositive: boolean | null
    negativeComments: string[]
    positiveComments: string[]
    emotionTags?: string[]
    followedPlan?: boolean | null
    brokenRules?: string[]
    journalNote?: string | null
  }
  tradingStrategy: { strategies: string[]; customStrategies: string[] }
  tradingConfluence: {
    tradingConfluences: string[]
    customTradingConfluences: string[]
  }
  riskManagement: { name: string; answer: string | boolean | null }[]
  accountId: number | undefined
}

export type MassFillJournalingRequestData = Omit<JournalingRequestData, 'tradingLogId'> & {
  selectedTrades: number[]
}

export interface JournalOptions {
  psychology: {
    isPositive: boolean | null
    negativeComments: { name: string; answer: boolean }[]
    positiveComments: { name: string; answer: boolean }[]
    emotionTags: { name: string; answer: boolean }[]
    followedPlan: boolean | null
    brokenRules: { name: string; answer: boolean }[]
    journalNote: string | null
    sentimentAnalysis: {
      sentimentLabel: string | null
      sentimentScore: number | null
      confidence: number | null
      disciplineScore: number | null
      analyzedAt: string | null
      suggestedEmotionTags: string[]
      suggestedRuleBreaks: string[]
      enabled: boolean
    }
  }
  tradingStrategy: {
    strategies: { name: string; answer: boolean }[]
    customStrategies: { name: string; answer: boolean }[]
  }
  tradingConfluence: {
    tradingConfluences: { name: string; answer: boolean }[]
    customTradingConfluences: { name: string; answer: boolean }[]
  }
  riskManagement: { name: string; answer: boolean | null }[]
}

export interface JournalingState {
  journalOptions: JournalOptions
}

export enum Psychology {
  positive = 'positive',
  negative = 'negative',
}

export enum RiskAnswer {
  yes = 'yes',
  no = 'no',
}

export type SelectColumnsModalProps = {
  isOpen: boolean
  closeModal: () => void
}

export type ColumnSettings = {
  [key in CommonTableColumnsEnum]: boolean
}

export type SharingFeatureProps = {
  id: number
  logEntry: Trade
}
